(() => {
  const initializedRoots = new WeakSet();

  function loadPrivacyApi() {
    return new Promise((resolve, reject) => {
      const existing = window.Shopify?.customerPrivacy;
      if (existing) {
        resolve(existing);
        return;
      }
      if (!window.Shopify?.loadFeatures) {
        reject(new Error("Shopify privacy controls are unavailable."));
        return;
      }
      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        (error) => {
          if (error || !window.Shopify?.customerPrivacy) {
            reject(new Error("Privacy choices could not be loaded. Please try again."));
            return;
          }
          resolve(window.Shopify.customerPrivacy);
        },
      );
    });
  }

  function explicitChoiceExists(consent) {
    return [consent?.preferences, consent?.analytics, consent?.marketing]
      .some((value) => value === "yes" || value === "no");
  }

  function initialize(root) {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const banner = root.querySelector("[data-cookie-banner]");
    const modal = root.querySelector("[data-cookie-modal]");
    const preferences = root.querySelector("[data-cookie-preferences]");
    const analytics = root.querySelector("[data-cookie-analytics]");
    const marketing = root.querySelector("[data-cookie-marketing]");
    const statusNodes = root.querySelectorAll("[data-cookie-status], [data-cookie-modal-status]");
    const choiceButtons = root.querySelectorAll("[data-cookie-accept], [data-cookie-reject], [data-cookie-save]");
    let privacyApi = null;
    let lastFocused = null;

    function setStatus(message = "") {
      statusNodes.forEach((node) => { node.textContent = message; });
    }

    function setBusy(busy) {
      choiceButtons.forEach((button) => { button.disabled = busy; });
    }

    function syncChoices() {
      if (!privacyApi) return;
      const consent = privacyApi.currentVisitorConsent();
      preferences.checked = consent.preferences === "yes";
      analytics.checked = consent.analytics === "yes";
      marketing.checked = consent.marketing === "yes";
    }

    function openModal() {
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.documentElement.style.overflow = "hidden";
      modal.querySelector(".y3dm-cookie-modal__close")?.focus();
      if (privacyApi) {
        syncChoices();
        return;
      }
      loadPrivacyApi().then((api) => {
        privacyApi = api;
        syncChoices();
      }).catch((error) => setStatus(error.message));
    }

    function closeModal() {
      modal.hidden = true;
      document.documentElement.style.overflow = "";
      lastFocused?.focus?.();
    }

    async function recordConsent(values) {
      setBusy(true);
      setStatus("");
      try {
        if (!privacyApi) privacyApi = await loadPrivacyApi();
        await new Promise((resolve) => privacyApi.setTrackingConsent(values, resolve));
        banner.hidden = true;
        closeModal();
      } catch (error) {
        setStatus(error.message || "Privacy choices could not be saved.");
      } finally {
        setBusy(false);
      }
    }

    root.querySelectorAll("[data-cookie-settings], [data-cookie-customize]").forEach((button) => {
      button.addEventListener("click", openModal);
    });
    root.querySelectorAll("[data-cookie-close]").forEach((button) => {
      button.addEventListener("click", closeModal);
    });
    root.querySelectorAll("[data-cookie-reject]").forEach((button) => {
      button.addEventListener("click", () => recordConsent({
        preferences: false,
        analytics: false,
        marketing: false,
      }));
    });
    root.querySelectorAll("[data-cookie-accept]").forEach((button) => {
      button.addEventListener("click", () => recordConsent({
        preferences: true,
        analytics: true,
        marketing: true,
      }));
    });
    root.querySelector("[data-cookie-save]")?.addEventListener("click", () => recordConsent({
      preferences: preferences.checked,
      analytics: analytics.checked,
      marketing: marketing.checked,
    }));

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !modal.hidden) closeModal();
      if (event.key !== "Tab" || modal.hidden) return;
      const focusable = [...modal.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href], select:not([disabled]), textarea:not([disabled])',
      )].filter((element) => !element.hidden);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (root.dataset.bannerEnabled !== "true") return;
    loadPrivacyApi().then((api) => {
      privacyApi = api;
      const consent = api.currentVisitorConsent();
      const globallyEnabled = root.dataset.showGlobally === "true";
      if (!explicitChoiceExists(consent) && (globallyEnabled || api.shouldShowBanner())) {
        banner.hidden = false;
      }
    }).catch((error) => {
      banner.hidden = false;
      setStatus(error.message);
      setBusy(false);
    });
  }

  function initializeAll(scope = document) {
    if (scope.matches?.("[data-y3dm-compliance]")) initialize(scope);
    scope.querySelectorAll?.("[data-y3dm-compliance]").forEach(initialize);
  }

  initializeAll();
  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
